import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import { CenterSpinner } from "~/atoms/spinner";
import { AdminConsentCard } from "~/molecules/onboarding/AdminConsentCard";
import { LegacyMigrationCard } from "~/molecules/onboarding/LegacyMigrationCard";
import { SignInCard } from "~/molecules/onboarding/SignInCard";
import { SpeSetupCard } from "~/molecules/onboarding/SpeSetupCard";
import {
  OnboardingSidebar,
  getCurrentStep,
  type StepId,
} from "~/molecules/onboarding/OnboardingSidebar";
import { LanguageSelector } from "~/molecules/LanguageSelector";
import { useMicrosoftIntegration } from "~/api/v2/useMicrosoftIntegration";

export default function MicrosoftOnboardingDashboard() {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const setupToken = searchParams.get("token");

  const { data, isLoading } = useMicrosoftIntegration(setupToken, {
    refreshInterval: 5000,
  });

  const status = searchParams.get("status");
  const [showErrorDialog, setShowErrorDialog] = useState(status === "error");
  const [currentStep, setCurrentStep] = useState<StepId | null>(null);

  if (!setupToken) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!data) {
    return (
      <div className="center grid h-screen place-items-center">
        {t("msIntegration.tokenNotFound")}
      </div>
    );
  }

  const activeStep = currentStep ?? getCurrentStep(data);

  const handleSkipUserMigration = () => {
    setCurrentStep("speSetup");
  };

  return (
    <>
      <LanguageSelector />
      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("msIntegration.error.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("msIntegration.error.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>
              {t("commons.buttons.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="mx-auto min-h-screen w-5xl px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold">{t("msIntegration.title")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("msIntegration.subtitle")}
          </p>
        </div>

        {/* Main content with sidebar */}
        <div className="grid gap-8 md:grid-cols-[280px_1fr]">
          {/* Sidebar */}
          <aside>
            <div className="sticky top-8">
              <OnboardingSidebar
                data={data}
                currentStep={activeStep}
                onStepClick={setCurrentStep}
              />
            </div>
          </aside>

          {/* Main content area */}
          <main className="min-w-0">
            {activeStep === "adminConsent" && (
              <AdminConsentCard data={data} setupToken={setupToken} />
            )}

            {activeStep === "userMigration" &&
              data.hasLegacyProvider &&
              (data.hasAccessToken ? (
                <LegacyMigrationCard
                  setupToken={setupToken}
                  onSkip={handleSkipUserMigration}
                />
              ) : (
                <SignInCard
                  setupToken={setupToken}
                  onSkip={handleSkipUserMigration}
                />
              ))}

            {activeStep === "speSetup" && (
              <SpeSetupCard data={data} setupToken={setupToken} />
            )}
          </main>
        </div>
      </div>
    </>
  );
}
